
    // IMPORTS
    import { computed, defineComponent, reactive } from 'vue'
    import { useRoute } from 'vue-router'
    import router from '@/router'
    import { useSessionStore } from '@/store/session'
    import { usePortalMessageStore } from '@/store/portalMessages'

    import { Input } from '@progress/kendo-vue-inputs'
    import { Button } from '@progress/kendo-vue-buttons'
    import { Error } from '@progress/kendo-vue-labels'
    import Loader from '@/components/common/loader.vue'
    import PartnerLoginForm from '@/components/auth/partner-login-form.vue'

    import { AuthenticationApi, buildLocalization, StringUtil } from '@/util'
    import { AxiosError, AxiosResponse, HttpStatusCode } from 'axios'
    import { ILoginResultDTO } from '@/types/auth'

    export default defineComponent({
        name: 'UnifiedLogin',

        components: {
            Loader,
            PartnerLoginForm,
            'k-input': Input,
            'k-button': Button,
            Error
        },

        setup() {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const { localize } = buildLocalization()
            const route = useRoute()
            const portalMessageStore = usePortalMessageStore()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const deviceState = reactive({
                serialNumber: (route.query.sn as string) || '',
                serialNoLoginAttempt: false,
                loginInProgress: false,
                loginErrorMessage: route.query.deviceError ? localize(route.query.deviceError as string) : ''
            })

            const publicData = { deviceState }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const maintenanceMessage = computed(() => portalMessageStore.maintenanceMessage)
            const isMaintenanceMode = computed(() => StringUtil.hasContent(maintenanceMessage.value))
            const serialNoHasContent = computed(() => StringUtil.hasContent(deviceState.serialNumber))
            const showSerialNoError = computed(() => !serialNoHasContent.value && deviceState.serialNoLoginAttempt)

            const computedData = { maintenanceMessage, isMaintenanceMode, showSerialNoError }

            /********************************************************************************************************************
             PUBLIC METHODS: DEVICE LOGIN
             ********************************************************************************************************************/
            function searchDevice() {
                deviceState.serialNoLoginAttempt = true
                if (serialNoHasContent.value) {
                    getDeviceToken()
                }
            }

            function onSerialNoInput() {
                deviceState.serialNoLoginAttempt = false
                deviceState.loginErrorMessage = ''
            }

            const publicMethods = { localize, searchDevice, onSerialNoInput }

            /********************************************************************************************************************
             PRIVATE METHODS: DEVICE LOGIN
             ********************************************************************************************************************/
            function getDeviceToken() {
                deviceState.loginInProgress = true
                const api = new AuthenticationApi()
                const serialNumber = deviceState.serialNumber.trim()

                api.getDeviceToken(serialNumber)
                    .then(response => handleDeviceResponse(response))
                    .catch(error => {
                        if (error instanceof AxiosError && error.response != null) {
                            handleDeviceResponse(error.response)
                        }
                    })
                    .finally(() => deviceState.loginInProgress = false)
            }

            function handleDeviceResponse(response: AxiosResponse<ILoginResultDTO>) {
                const sessionStore = useSessionStore()
                deviceState.loginErrorMessage = ''
                const serialNumber = deviceState.serialNumber.trim()

                switch (response.status) {
                    case HttpStatusCode.Ok:
                        sessionStore.setDeviceLogin(response.data, serialNumber)
                        router.push({ name: 'equipment-documents', params: { sn: serialNumber } })
                        break
                    case HttpStatusCode.Unauthorized:
                        deviceState.loginErrorMessage = localize('UnknownDevice')
                        break
                    case HttpStatusCode.Forbidden:
                        deviceState.loginErrorMessage = localize('AccessBlocked')
                        break
                    default:
                        deviceState.loginErrorMessage = localize('UnexpectedError')
                        break
                }
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
